import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { User, Mail, Shield, Calendar, Briefcase, AlertCircle } from 'lucide-react';

const Profile = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        console.log("🔍 Fetching user profile...");
        const response = await api.get('/users/me/');
        console.log("✅ Profile Data Received:", response.data);
        setUser(response.data);
      } catch (err) {
        console.error("❌ Error fetching profile:", err);
        setError("Failed to load profile information.");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-red-500">
        <AlertCircle size={48} className="mb-4" />
        <p>{error}</p>
      </div>
    );
  }

  if (!user) {
    return <div className="text-center text-gray-500 mt-10">No user data found.</div>;
  }

  // Determine role display
  const roleDisplay = user.is_superuser ? 'Administrator' : (user.is_officer ? 'Recovery Officer' : 'User');
  const roleColor = user.is_superuser ? 'text-blue-600' : (user.is_officer ? 'text-green-600' : 'text-gray-600');
  const bannerClass = user.is_superuser 
    ? 'bg-gradient-to-r from-blue-600 to-blue-800' 
    : 'bg-gradient-to-r from-green-600 to-green-800';

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        
        {/* Header Banner */}
        <div className={`h-32 relative ${bannerClass}`}>
          <div className="absolute -bottom-12 left-8">
            <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center border-4 border-white shadow-md">
              <span className={`text-3xl font-bold ${user.is_superuser ? 'text-blue-700' : 'text-green-700'}`}>
                {user.username ? user.username[0].toUpperCase() : 'U'}
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="pt-16 px-8 pb-8">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{user.username}</h1>
              <p className={`text-sm font-medium ${roleColor} capitalize`}>{roleDisplay}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            
            {/* Personal Info Card */}
            <div className="p-6 bg-gray-50 rounded-lg border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <User size={20} className="text-blue-600" />
                Personal Information
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Full Name</label>
                  <p className="text-gray-900 font-medium">
                    {user.first_name && user.last_name 
                      ? `${user.first_name} ${user.last_name}` 
                      : user.username || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Username</label>
                  <p className="text-gray-900 font-medium">{user.username}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Email Address</label>
                  <p className="text-gray-900 font-medium flex items-center gap-2">
                    <Mail size={16} className="text-gray-400" />
                    {user.email || 'Not provided'}
                  </p>
                </div>
              </div>
            </div>

            {/* Account Details Card */}
            <div className="p-6 bg-gray-50 rounded-lg border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Shield size={20} className="text-green-600" />
                Account Details
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Role</label>
                  <p className="text-gray-900 font-medium capitalize flex items-center gap-2">
                    {user.is_superuser ? <Shield size={16} /> : <Briefcase size={16} />}
                    {roleDisplay}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Status</label>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Active
                  </span>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">User ID</label>
                  <p className="text-gray-900 font-medium flex items-center gap-2">
                    <Calendar size={16} className="text-gray-400" />
                    #{user.id}
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;